import { OIDC_CLAIM_MAPPING_FIELDS, OIDC_SCOPES_MAX, type OidcMappedField } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Field, Icon, Input, Switch } from "@sagot/ui";
import { useId, useState } from "react";
import { CardSkeleton, CopyTextButton, Gate, PanelError, useCanManage } from "../panels/panel-kit";
import { draftOf, isDirty, problemsOf, toBody, type OidcDraft, type OidcProblem } from "./oidc-model";
import { useOidcSettings, useSaveOidcSettings, type OidcAgent, type OidcSettings } from "./use-oidc-settings";

const K = "launch.oidc";
const FIELD_KEYS: Readonly<Record<OidcMappedField, string>> = { ref: "mapRef", tier: "mapTier", account_status: "mapStatus" };
const PROBLEM_KEYS: Readonly<Record<OidcProblem, string>> = {
  issuerInvalid: "problemIssuerInvalid",
  issuerHttp: "problemIssuerHttp",
  clientIdMissing: "problemClientId",
  secretMissing: "problemSecret",
  scopesTooMany: "problemScopes",
  mappingMissing: "problemMapping",
};

/** One agent's two addresses: the one the operator registers at its provider and the one its site links to. */
function AgentAddresses({ agent }: { agent: OidcAgent }) {
  const { t } = useTranslation();
  return (
    <li className="rounded-md border border-line bg-sunken p-3" data-testid={`oidc-agent-${agent.agent_key}`}>
      <p className="text-sm font-semibold">{agent.name}</p>
      <ul className="mt-2 space-y-2 text-xs">
        {([["redirect", agent.redirect_uri], ["start", agent.start_url]] as const).map(([kind, address]) => (
          <li key={kind} className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-fg-muted">{t(`${K}.${kind}Label`)}</p>
              <p className="break-all font-mono text-fg" data-testid={`oidc-${kind}-${agent.agent_key}`}>
                {address}
              </p>
            </div>
            <CopyTextButton value={address} label={t(`${K}.copy`)} className="h-8 px-3 text-xs" />
          </li>
        ))}
      </ul>
    </li>
  );
}

function statusOf(settings: OidcSettings): { tone: "success" | "neutral" | "warning"; key: string } {
  if (!settings.configured) return { tone: "neutral", key: "statusNotSet" };
  return settings.active ? { tone: "success", key: "statusOn" } : { tone: "warning", key: "statusOff" };
}

/**
 * Secure launch "Sign in with your OpenID provider" card (PID-009, contract F20): the workspace's own OpenID provider as a
 * second way for a customer to arrive. Authorization code with PKCE; the claims of the verified ID token are mapped to the
 * customer reference, tier and account status by the names typed here. The client secret is write-only. Inside a frame the
 * provider cannot be shown, so the chat falls back to launch codes there.
 */
export function OidcCard({ settings }: { settings: OidcSettings }) {
  const { t } = useTranslation();
  const manage = useCanManage("customers.manage_launch");
  const saver = useSaveOidcSettings(t(`${K}.saved`));
  const id = useId();
  const [draft, setDraft] = useState<OidcDraft | null>(null);
  const [shown, setShown] = useState(false);
  const current = draft ?? draftOf(settings);
  const dirty = draft !== null && isDirty(draft, settings);
  const problems = problemsOf(current, settings);
  const status = statusOf(settings);
  const edit = (change: Partial<OidcDraft>) => {
    setDraft({ ...current, ...change });
    saver.clearFailure();
  };
  const reset = () => {
    setDraft(null);
    setShown(false);
    saver.clearFailure();
  };
  const save = async () => {
    setShown(true);
    if (problems.length > 0) return;
    if (await saver.save(toBody(current))) reset();
  };
  const problem = (wanted: readonly OidcProblem[]): string | undefined => {
    if (!shown) return undefined;
    const found = problems.find((candidate) => wanted.includes(candidate));
    return found ? t(`${K}.${PROBLEM_KEYS[found]}`) : undefined;
  };
  return (
    <section className="card" aria-labelledby={`${id}-title`} data-testid="oidc-card">
      <div className="card-header flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`${id}-title`} className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
        <Badge tone={status.tone} dot data-testid="oidc-status">
          {t(`${K}.${status.key}`)}
        </Badge>
      </div>
      <div className="space-y-5 px-5 py-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t(`${K}.issuerLabel`)} hint={t(`${K}.issuerHint`)} error={problem(["issuerInvalid", "issuerHttp"])}>
            <Input type="url" inputMode="url" autoComplete="off" spellCheck={false} className="font-mono" placeholder={t(`${K}.issuerPlaceholder`)} value={current.issuer} disabled={!manage.allowed} onChange={(event) => { edit({ issuer: event.target.value }); }} data-testid="oidc-issuer" />
          </Field>
          <Field label={t(`${K}.clientIdLabel`)} error={problem(["clientIdMissing"])}>
            <Input autoComplete="off" spellCheck={false} className="font-mono" value={current.clientId} disabled={!manage.allowed} onChange={(event) => { edit({ clientId: event.target.value }); }} data-testid="oidc-client-id" />
          </Field>
          <Field label={t(`${K}.secretLabel`)} hint={settings.has_secret ? t(`${K}.secretStored`) : t(`${K}.secretHint`)} error={problem(["secretMissing"])}>
            <Input type="password" autoComplete="new-password" spellCheck={false} className="font-mono" placeholder={settings.has_secret ? t(`${K}.secretKeep`) : ""} value={current.clientSecret} disabled={!manage.allowed} onChange={(event) => { edit({ clientSecret: event.target.value }); }} data-testid="oidc-client-secret" />
          </Field>
          <Field label={t(`${K}.scopesLabel`)} hint={t(`${K}.scopesHint`, { max: OIDC_SCOPES_MAX })} error={problem(["scopesTooMany"])}>
            <Input autoComplete="off" spellCheck={false} className="font-mono" placeholder="email profile" value={current.scopes} disabled={!manage.allowed} onChange={(event) => { edit({ scopes: event.target.value }); }} data-testid="oidc-scopes" />
          </Field>
        </div>
        <fieldset className="space-y-3" data-testid="oidc-mapping">
          <legend className="text-sm font-semibold">{t(`${K}.mappingTitle`)}</legend>
          <p className="text-xs text-fg-muted">{t(`${K}.mappingHint`)}</p>
          <div className="grid gap-4 md:grid-cols-3">
            {OIDC_CLAIM_MAPPING_FIELDS.map((field) => (
              <Field key={field} label={t(`${K}.${FIELD_KEYS[field]}`)} error={field === "ref" ? problem(["mappingMissing"]) : undefined}>
                <Input autoComplete="off" spellCheck={false} className="font-mono" value={current.mapping[field]} disabled={!manage.allowed} onChange={(event) => { edit({ mapping: { ...current.mapping, [field]: event.target.value } }); }} data-testid={`oidc-map-${field}`} />
              </Field>
            ))}
          </div>
        </fieldset>
        <div className="flex items-start justify-between gap-4 border-t border-line pt-4">
          <label htmlFor={`${id}-active`} className="min-w-0 text-sm">
            <span id={`${id}-active-name`} className="font-semibold">{t(`${K}.activeTitle`)}</span>
            <span id={`${id}-active-desc`} className="block text-fg-muted">{t(`${K}.activeHint`)}</span>
          </label>
          <Switch id={`${id}-active`} aria-labelledby={`${id}-active-name`} aria-describedby={`${id}-active-desc`} checked={current.active} disabled={!manage.allowed} onCheckedChange={(on) => { edit({ active: on }); }} data-testid="oidc-active" />
        </div>
        <div className="space-y-2" data-testid="oidc-addresses">
          <h3 className="text-sm font-semibold">{t(`${K}.addressesTitle`)}</h3>
          <p className="text-xs text-fg-muted">{t(`${K}.addressesHint`)}</p>
          {settings.agents.length === 0 ? (
            <p className="text-xs text-fg-muted">{t(`${K}.addressesEmpty`)}</p>
          ) : (
            <ul className="space-y-2">
              {settings.agents.map((agent) => (
                <AgentAddresses key={agent.agent_key} agent={agent} />
              ))}
            </ul>
          )}
        </div>
        <div role="note" className="flex items-start gap-2.5 rounded-md bg-info-soft p-3 text-sm" data-testid="oidc-frame-note">
          <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
          <p className="min-w-0 text-fg-muted">{t(`${K}.frameNote`)}</p>
        </div>
        {manage.allowed ? null : <p className="text-xs text-fg-muted" data-testid="oidc-read-only">{manage.sessionReadOnly ? manage.reason : t(`${K}.readOnly`)}</p>}
        {saver.failure ? (
          <p role="alert" className="text-xs font-semibold text-danger" data-testid="oidc-error">
            {saver.failure}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="secondary" disabled={!dirty || saver.busy} onClick={reset}>
          {t(`${K}.discard`)}
        </Button>
        <Gate reason={manage.reason}>
          <Button loading={saver.busy} disabled={!manage.allowed || !dirty} onClick={() => void save()}>
            {saver.busy ? t(`${K}.saving`) : t(`${K}.save`)}
          </Button>
        </Gate>
      </div>
    </section>
  );
}

/** The card with its own load: a skeleton while it reads, a retryable error, then the card. */
export function OidcSlot() {
  const { t } = useTranslation();
  const probe = useOidcSettings();
  if (probe.isPending) return <CardSkeleton rows={4} />;
  if (probe.isError) return <PanelError error={probe.error} onRetry={() => void probe.refetch()} title={t(`${K}.loadError`)} />;
  return <OidcCard settings={probe.data} />;
}
